import {
  createController,
  currentMonthFirst,
  emptyOptionSearch,
  optionQueryAction,
  parseMonth,
} from '@eo/shared';

import {
  fetchClientRows,
  fetchEntryRows,
  fetchExcessBill,
  fetchReport,
  generateExcessBill,
  searchClients,
  searchClientTasks,
  searchPeople,
  setClientHours,
  shareClient,
  syncMonth,
  unshareClient,
} from './api/api';
import type { ExcessBill } from './api/types';
import { clientListParams, entryListParams, parseDetailPane, parseSelection } from './helpers';
import type {
  CapacityState,
  GeneratePayload,
  LoadKeys,
  PlanChange,
  PlanPayload,
  SyncPayload,
} from './types';

const initialState: CapacityState = {
  options: {
    clients: emptyOptionSearch(),
    people: emptyOptionSearch(),
    clientTasks: emptyOptionSearch(),
  },
  month: currentMonthFirst(),
  report: null,
  clientRows: [],
  clientsPagination: null,
  entryRows: [],
  entriesPagination: null,
  excessBill: null,
  loadedFor: null,
};

// The report is the expensive part, so each piece is fetched again only when
// its own inputs changed: sorting the log or opening a client leaves the report
// as it is. A null `previous` fetches everything; a bill already in hand is not
// fetched again.
async function load(
  month: string,
  owner: string | undefined,
  query: Record<string, string>,
  previous: CapacityState | null,
  billInHand: ExcessBill | null = null,
) {
  // The log is detail someone asks for: it loads only when a client's or a
  // person's Time log is open.
  const selection = parseSelection(query);
  const entryParams =
    selection !== null && parseDetailPane(query.pane) === 'log'
      ? entryListParams(selection, query)
      : null;
  // The bill reads ClickUp, so it loads only when a client's Excess hours is open.
  const billClient =
    selection?.kind === 'client' && parseDetailPane(query.pane) === 'excess' ? selection.id : null;
  const clientParams = clientListParams(query);
  const keys: LoadKeys = {
    report: JSON.stringify([month, owner]),
    clients: JSON.stringify([month, owner, clientParams]),
    entries: entryParams && JSON.stringify([month, owner, entryParams]),
    excess: billClient && JSON.stringify([month, billClient]),
  };
  const fresh = (key: keyof LoadKeys) => previous?.loadedFor?.[key] !== keys[key];

  const [report, clients, entries, excessBill] = await Promise.all([
    fresh('report') ? fetchReport(month, owner) : null,
    fresh('clients') ? fetchClientRows(month, owner, clientParams) : null,
    entryParams && fresh('entries') ? fetchEntryRows(month, owner, entryParams) : null,
    billClient && (billInHand ?? (fresh('excess') ? fetchExcessBill(billClient, month) : null)),
  ]);
  return {
    loadedFor: keys,
    ...(report && { report }),
    ...(clients && { clientRows: clients.clients, clientsPagination: clients.pagination }),
    ...(entries && { entryRows: entries.entries, entriesPagination: entries.pagination }),
    ...(!entryParams && { entryRows: [], entriesPagination: null }),
    ...(excessBill && { excessBill }),
    ...(!billClient && { excessBill: null }),
  };
}

function applyPlan(change: PlanChange, month: string): Promise<void> {
  switch (change.kind) {
    case 'hours':
      return setClientHours({
        clientId: change.clientId,
        effectiveMonth: month,
        fixedHours: change.fixedHours,
        variableHours: change.variableHours,
      });
    case 'share':
      return shareClient({
        clientId: change.clientId,
        userId: change.userId,
        fixedHours: change.fixedHours,
        variableHours: change.variableHours,
      });
    case 'unshare':
      return unshareClient(change.assignmentId);
  }
}

export const capacityController = createController<CapacityState>({
  name: 'capacity',
  state: initialState,
  fetchData: async ({ queryParams, data }) => {
    const params = (queryParams ?? {}) as Record<string, string>;
    const month = parseMonth(params.month);

    return {
      month,
      ...(await load(month, params.cfo, params, (data as CapacityState | undefined) ?? null)),
    };
  },
  asyncActions: {
    queryOptions: optionQueryAction<CapacityState>({
      clients: searchClients,
      people: searchPeople,
      clientTasks: searchClientTasks,
    }),
    // Every figure a plan touches is computed on the server, so the page reads
    // the report and the clients again rather than patching them.
    planClient: {
      action: async (payload) => {
        const { change, month, query } = payload as PlanPayload;
        await applyPlan(change, month);
        return load(month, query.cfo, query, null);
      },
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          ...(action as { payload: Omit<CapacityState, 'month'> }).payload,
        }),
      },
    },
    // The refreshed report is refetched rather than patched in: the sync
    // rewrites entries, and every derived number is computed server-side.
    syncMonth: {
      action: async (payload) => {
        const { month, query } = payload as SyncPayload;
        await syncMonth(month);
        return load(month, query.cfo, query, null);
      },
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          ...(action as { payload: Omit<CapacityState, 'month'> }).payload,
        }),
      },
    },
    // Generating rewrites the client's entries in ClickUp and re-syncs them, so
    // the report and the clients grid are refetched with the bill it returns.
    generateExcess: {
      action: async (payload) => {
        const { body, month, query } = payload as GeneratePayload;
        const bill = await generateExcessBill(body);
        return load(month, query.cfo, query, null, bill);
      },
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          ...(action as { payload: Omit<CapacityState, 'month'> }).payload,
        }),
      },
    },
  },
});
